import { useCallback, useRef, useState } from 'react';
import { GetToken } from '@utils/storage';
import * as config from '@config';

// Mirrors backend request.CreateTransferEnquiryReq from
// internal/services/transferenquiry/request/create.go.
export interface CreateTransferEnquiryRequest {
    date: string;
    time: string;
    retDate?: string;
    retTime?: string;
    pickup: string;
    dropoff: string;
    hours?: string;
    passengerCount: string;
    luggageCount: string;
}

export interface CreateTransferEnquiryResult {
    enquiryId: string;
}

interface UseCreateTransferEnquiry {
    submitting: boolean;
    create: (req: CreateTransferEnquiryRequest) => Promise<CreateTransferEnquiryResult>;
}

// useCreateTransferEnquiry is the TS replacement for _useTransferEnquiryPost.js.
// Used by the transfer page when the agent is not eligible for instant booking
// and submitting goes straight to the enquiry pipeline.
function useCreateTransferEnquiry(): UseCreateTransferEnquiry {
    const [submitting, setSubmitting] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    const create = useCallback(async (req: CreateTransferEnquiryRequest): Promise<CreateTransferEnquiryResult> => {
        setSubmitting(true);
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        try {
            const response = await fetch(`${(config as { apiURL2: string }).apiURL2}/transfer-enquiries`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${GetToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(req),
                signal: abortRef.current.signal,
            });
            const body = await response.json().catch(() => ({} as Record<string, unknown>));
            if (!response.ok) {
                const b = body as { error?: string; message?: string };
                throw new Error(b.error ?? b.message ?? `提交失败 (${response.status})`);
            }
            const b = body as { enquiryId?: string; id?: string };
            return { enquiryId: b.enquiryId ?? b.id ?? '' };
        } finally {
            setSubmitting(false);
        }
    }, []);

    return { submitting, create };
}

export default useCreateTransferEnquiry;
